import { Label } from '@creanizer/ui'
import { Card } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { RadioGroup, RadioGroupItem } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Settings } from 'lucide-react'
import type { CommunityInput, CommunityTemplate, SavedWorldBuilding, SavedCommunity, SavedCharacter, SavedEnemy } from '@creanizer/types'
import { WorldContextForm } from './WorldContextForm'
import { CommunityContextForm } from './CommunityContextForm'
import { CharacterContextForm } from './CharacterContextForm'
import { EnemyContextForm } from './EnemyContextForm'

interface TemplateModeFormProps {
  input: CommunityInput
  onChange: (input: CommunityInput) => void
  savedWorlds?: SavedWorldBuilding[]
  savedCommunities?: SavedCommunity[]
  characters?: SavedCharacter[]
  enemies?: SavedEnemy[]
}

interface Template {
  id: CommunityTemplate
  name: string
  description: string
  tags: string[]
  scale: 'tiny' | 'small' | 'medium' | 'large' | 'huge' | 'massive'
  communityType: string
  category: 'fantasy' | 'modern' | 'cyberpunk' | 'special'
}

const templates: Template[] = [
  // ファンタジー系
  {
    id: 'kingdom_empire',
    name: '王国・帝国',
    description: '君主制の大規模な国家',
    tags: ['王権', '貴族', '軍事'],
    scale: 'massive',
    communityType: 'nation',
    category: 'fantasy',
  },
  {
    id: 'city_states',
    name: '小国家連合',
    description: '複数の都市国家による連合体',
    tags: ['同盟', '自治', '交易'],
    scale: 'huge',
    communityType: 'nation',
    category: 'fantasy',
  },
  {
    id: 'noble_domain',
    name: '貴族領',
    description: '貴族が統治する領地',
    tags: ['封建制', '領主', '農民'],
    scale: 'large',
    communityType: 'city',
    category: 'fantasy',
  },
  {
    id: 'province_state',
    name: '州・省',
    description: '国の下位に位置する大規模な行政区域',
    tags: ['行政', '地方政府', '複数都市'],
    scale: 'huge',
    communityType: 'state',
    category: 'fantasy',
  },
  {
    id: 'feudal_territory',
    name: '封建領地',
    description: '諸侯が統治する広大な領土',
    tags: ['封建制', '諸侯', '複数の町村'],
    scale: 'huge',
    communityType: 'territory',
    category: 'fantasy',
  },
  {
    id: 'frontier_territory',
    name: '辺境領',
    description: '国境地帯の開拓領地',
    tags: ['開拓', '防衛', '自治'],
    scale: 'large',
    communityType: 'territory',
    category: 'fantasy',
  },
  {
    id: 'medieval_city',
    name: '中世の都市',
    description: '城壁に囲まれた大規模な都市',
    tags: ['商業', 'ギルド', '市民'],
    scale: 'large',
    communityType: 'city',
    category: 'fantasy',
  },
  {
    id: 'medieval_town',
    name: '中世の町',
    description: '活気ある中規模の町',
    tags: ['市場', '職人', '宿屋'],
    scale: 'medium',
    communityType: 'town',
    category: 'fantasy',
  },
  {
    id: 'medieval_village',
    name: '中世の村',
    description: '農業を中心とした小さな共同体',
    tags: ['農業', '共同体', '教会'],
    scale: 'small',
    communityType: 'village',
    category: 'fantasy',
  },
  {
    id: 'medieval_slum',
    name: '中世のスラム',
    description: '都市の片隅にある貧民街',
    tags: ['貧困', '犯罪', '生存'],
    scale: 'small',
    communityType: 'hamlet',
    category: 'fantasy',
  },
  {
    id: 'adventurer_town',
    name: '冒険者の集う町',
    description: '冒険者たちが拠点とする町',
    tags: ['冒険者', 'ギルド', 'ダンジョン'],
    scale: 'medium',
    communityType: 'town',
    category: 'fantasy',
  },
  
  // 現代系
  {
    id: 'modern_nation',
    name: '現代国家',
    description: '民主制や独裁制などの現代的な国家',
    tags: ['政府', '経済', '国際'],
    scale: 'massive',
    communityType: 'nation',
    category: 'modern',
  },
  {
    id: 'megalopolis',
    name: 'メガロポリス',
    description: '巨大都市圏・首都',
    tags: ['高層ビル', '金融', 'グローバル'],
    scale: 'huge',
    communityType: 'metropolis',
    category: 'modern',
  },
  {
    id: 'regional_city',
    name: '地方都市',
    description: '地方の中核となる都市',
    tags: ['行政', '商業', '教育'],
    scale: 'large',
    communityType: 'city',
    category: 'modern',
  },
  {
    id: 'bedroom_town',
    name: 'ベッドタウン',
    description: '大都市近郊の住宅都市',
    tags: ['通勤', '住宅地', 'ファミリー'],
    scale: 'medium',
    communityType: 'town',
    category: 'modern',
  },
  {
    id: 'country_town',
    name: '田舎町',
    description: 'のどかな地方の小さな町',
    tags: ['農業', '高齢化', '伝統'],
    scale: 'small',
    communityType: 'town',
    category: 'modern',
  },
  {
    id: 'industrial_city',
    name: '工業都市',
    description: '製造業・工場地帯',
    tags: ['工場', '労働者', '産業'],
    scale: 'large',
    communityType: 'city',
    category: 'modern',
  },
  {
    id: 'tourist_spot',
    name: '観光地',
    description: '観光を主産業とする地域',
    tags: ['観光', 'ホテル', 'サービス'],
    scale: 'medium',
    communityType: 'town',
    category: 'modern',
  },
  {
    id: 'modern_state',
    name: '州（現代）',
    description: '連邦制国家の州や自治州',
    tags: ['州政府', '複数都市', '地方自治'],
    scale: 'huge',
    communityType: 'state',
    category: 'modern',
  },
  {
    id: 'administrative_region',
    name: '行政区域',
    description: '県や省などの行政区画',
    tags: ['地方行政', '県庁所在地', '複数市町村'],
    scale: 'huge',
    communityType: 'territory',
    category: 'modern',
  },
  {
    id: 'slum_area',
    name: 'スラム街',
    description: '都市の貧困地域',
    tags: ['貧困', '犯罪', '格差'],
    scale: 'medium',
    communityType: 'city',
    category: 'modern',
  },
  {
    id: 'modern_village',
    name: '村（現代）',
    description: '現代の小さな農村',
    tags: ['過疎化', '農業', '自然'],
    scale: 'tiny',
    communityType: 'village',
    category: 'modern',
  },
  {
    id: 'remote_island',
    name: '離島',
    description: '本土から離れた島',
    tags: ['孤立', '漁業', '独自文化'],
    scale: 'small',
    communityType: 'island',
    category: 'modern',
  },
  
  // サイバーパンク・SF系
  {
    id: 'megacorp_city',
    name: 'メガコーポ支配都市',
    description: '巨大企業が支配する都市',
    tags: ['企業', 'AI', '監視社会'],
    scale: 'huge',
    communityType: 'metropolis',
    category: 'cyberpunk',
  },
  {
    id: 'cyberpunk_city',
    name: 'サイバーパンク都市',
    description: 'ネオンと闇が混在する未来都市',
    tags: ['ハッカー', 'サイボーグ', 'スラム'],
    scale: 'large',
    communityType: 'city',
    category: 'cyberpunk',
  },
  {
    id: 'space_colony',
    name: 'スペースコロニー',
    description: '宇宙空間の居住施設',
    tags: ['宇宙', '人工重力', '閉鎖環境'],
    scale: 'medium',
    communityType: 'city',
    category: 'cyberpunk',
  },
  {
    id: 'underground_city',
    name: '地下都市',
    description: '地下に築かれた都市',
    tags: ['地下', '閉鎖的', '独特'],
    scale: 'large',
    communityType: 'city',
    category: 'cyberpunk',
  },
  {
    id: 'floating_city',
    name: '浮遊都市',
    description: '空中に浮かぶ都市',
    tags: ['空中', '先進的', '隔絶'],
    scale: 'medium',
    communityType: 'city',
    category: 'cyberpunk',
  },
  {
    id: 'ruined_city',
    name: '廃墟都市',
    description: '崩壊後の都市',
    tags: ['ポストアポカリプス', 'サバイバル', '廃墟'],
    scale: 'medium',
    communityType: 'city',
    category: 'cyberpunk',
  },
  {
    id: 'arcology',
    name: 'アーコロジー',
    description: '自己完結型巨大建築都市',
    tags: ['環境制御', '階層社会', '自給自足'],
    scale: 'large',
    communityType: 'city',
    category: 'cyberpunk',
  },
  {
    id: 'cyberspace_city',
    name: 'サイバー空間都市',
    description: 'デジタル世界の都市',
    tags: ['仮想現実', 'データ', 'AI'],
    scale: 'medium',
    communityType: 'city',
    category: 'cyberpunk',
  },
  // 特殊系
  {
    id: 'nomadic_camp',
    name: '遊牧民の野営地',
    description: '移動生活を営む集団の拠点',
    tags: ['遊牧', '移動', '部族'],
    scale: 'small',
    communityType: 'hamlet',
    category: 'special',
  },
  {
    id: 'ruins_settlement',
    name: '廃墟集落',
    description: '古代遺跡に築かれた集落',
    tags: ['遺跡', '神秘', '危険'],
    scale: 'small',
    communityType: 'hamlet',
    category: 'special',
  },
  {
    id: 'maritime_city',
    name: '海上都市',
    description: '海の上に浮かぶ都市',
    tags: ['海洋', '船', '水上生活'],
    scale: 'medium',
    communityType: 'city',
    category: 'special',
  },
  {
    id: 'treetop_city',
    name: '樹上都市',
    description: '巨大樹や森の上に築かれた都市',
    tags: ['森', '自然共生', '垂直建築'],
    scale: 'small',
    communityType: 'town',
    category: 'special',
  },
  {
    id: 'otherworld_town',
    name: '異界の街',
    description: '異次元や夢の中の街',
    tags: ['異次元', '非現実', '不思議'],
    scale: 'medium',
    communityType: 'town',
    category: 'special',
  },
  {
    id: 'refugee_camp',
    name: '難民キャンプ',
    description: '難民や避難民の仮設居住地',
    tags: ['仮設', '国際援助', '困窮'],
    scale: 'medium',
    communityType: 'hamlet',
    category: 'special',
  },
  {
    id: 'prison_city',
    name: '監獄都市',
    description: '巨大な刑務所や収容所',
    tags: ['監獄', '管理社会', '隔離'],
    scale: 'large',
    communityType: 'city',
    category: 'special',
  },
]

const categoryLabels = {
  fantasy: 'ファンタジー',
  modern: '現代',
  cyberpunk: 'サイバーパンク・SF',
  special: '特殊',
}

export function TemplateModeForm({ input, onChange, savedWorlds = [], savedCommunities = [], characters = [], enemies = [] }: TemplateModeFormProps) {
  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId)
    if (template) {
      onChange({
        ...input,
        templateType: template.id,
        communityType: template.communityType as any,
        scale: template.scale,
      })
    }
  }

  // カテゴリごとにテンプレートをグループ化
  const templatesByCategory = templates.reduce((acc, template) => {
    if (!acc[template.category]) {
      acc[template.category] = []
    }
    acc[template.category].push(template)
    return acc
  }, {} as Record<string, Template[]>)

  return (
    <div className="space-y-4">
      {/* 世界観連携 */}
      <WorldContextForm
        input={input}
        onChange={onChange}
        savedWorlds={savedWorlds}
      />
      
      {/* 関連コミュニティ連携 */}
      <CommunityContextForm
        input={input}
        onChange={onChange}
        savedCommunities={savedCommunities}
      />
      
      {/* 関連キャラクター連携 */}
      <CharacterContextForm
        input={input}
        onChange={onChange}
        characters={characters}
      />
      
      {/* 関連エネミー連携 */}
      <EnemyContextForm
        input={input}
        onChange={onChange}
        enemies={enemies}
      />
      
      <div>
        <h3 className="text-sm font-semibold mb-2">テンプレートを選択</h3>
        <p className="text-sm text-muted-foreground mb-4">
          様々なジャンルの共同体テンプレートから選んで生成できます
        </p>
      </div>

      <Tabs defaultValue="fantasy" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          {Object.entries(categoryLabels).map(([key, label]) => (
            <TabsTrigger key={key} value={key}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.entries(templatesByCategory).map(([category, categoryTemplates]) => (
          <TabsContent key={category} value={category} className="space-y-3">
            <RadioGroup
              value={input.templateType || ''}
              onValueChange={handleTemplateSelect}
            >
              <div className="grid gap-3">
                {categoryTemplates.map((template) => (
                  <Card
                    key={template.id}
                    className={`p-4 cursor-pointer transition-colors ${
                      input.templateType === template.id ? 'border-primary' : ''
                    }`}
                    onClick={() => handleTemplateSelect(template.id)}
                  >
                    <div className="flex items-start space-x-3">
                      <RadioGroupItem value={template.id} id={template.id} />
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor={template.id} className="font-semibold cursor-pointer">
                            {template.name}
                          </Label>
                          <div className="flex gap-1">
                            {template.tags.map((tag) => (
                              <Badge key={tag} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {template.description}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </RadioGroup>
          </TabsContent>
        ))}
      </Tabs>

      {input.templateType && (
        <div className="p-4 bg-muted rounded-lg">
          <p className="text-sm">
            選択されたテンプレート: <strong>{templates.find(t => t.id === input.templateType)?.name}</strong>
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            このテンプレートの特徴を活かした共同体が生成されます。
            ジャンルに応じた適切な名前と世界観で構築されます。
          </p>
        </div>
      )}
      
      {/* 追加設定 */}
      <div className="space-y-4 border-t pt-4">
        <div className="flex items-center space-x-2">
          <Settings className="h-4 w-4" />
          <Label>追加設定（オプション）</Label>
        </div>
        
        <div className="space-y-2">
          <Textarea
            value={input.additionalRequirements || ''}
            onChange={(e) => onChange({ ...input, additionalRequirements: e.target.value })}
            placeholder="テンプレートに加えて追加したい要素を記述&#10;例：&#10;・山の頂上に位置する&#10;・特定の種族が多く住んでいる&#10;・近くに古代遺跡がある&#10;・特産品がある（ワイン、薬草、魔法アイテムなど）&#10;・特定のギルドや組織の本部がある"
            rows={5}
          />
          <p className="text-xs text-muted-foreground">
            テンプレートの基本設定に加えて、さらに詳細な指示や要望を追加できます。
          </p>
        </div>
      </div>
    </div>
  )
}